import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { App, Button, Dropdown, Empty, Popconfirm, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { deleteBlock, listTemplates, reorderBlocks, saveBlock } from '../api';
import BlockForm from '../components/block-form';
import {
  BLOCK_KINDS,
  BLOCK_KIND_META,
  GalaBlock,
  GalaBlockKind,
  VISIBILITY_OPTIONS,
  blockSummary,
  errorText,
  guestGroupOptions,
  moveId,
} from '../contract';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
  blocks: GalaBlock[];
  onChanged: () => void | Promise<void>;
}

const visibilityText = (b: GalaBlock) => {
  const base = VISIBILITY_OPTIONS.find((o) => o.value === b.visibility)?.label ?? b.visibility;
  return b.visibility === 'GROUPS' && b.groups?.length ? `${base}: ${b.groups.join(', ')}` : base;
};

/** Tab Nội dung: danh sách khối, dời lên / xuống, thêm / sửa / xoá / ẩn. */
const BlocksTab = ({ eventId, blocks, onChanged }: Props) => {
  const { message } = App.useApp();
  // `editing` giữ nguyên khi đóng ngăn kéo để tiêu đề không nhảy trong lúc nó trượt ra.
  const [editing, setEditing] = useState<{ block: GalaBlock | null; kind: GalaBlockKind }>({
    block: null,
    kind: 'TEXT',
  });
  const [formOpen, setFormOpen] = useState(false);
  const openForm = (block: GalaBlock | null, kind: GalaBlockKind) => {
    setEditing({ block, kind });
    setFormOpen(true);
  };
  const [busyId, setBusyId] = useState<number | null>(null);
  const [templateGroups, setTemplateGroups] = useState<string[]>([]);

  // Gợi ý nhóm khách thêm các nhóm của mẫu vé (như tab Nhắc lịch); lỗi thì thôi, ô nhóm vẫn gõ tay được.
  useEffect(() => {
    let alive = true;
    listTemplates(eventId)
      .then((ts) => {
        if (alive) setTemplateGroups(ts.map((t) => t.guestGroup).filter((g): g is string => !!g));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [eventId]);

  const run = async (id: number, task: () => Promise<unknown>, fail: string) => {
    setBusyId(id);
    try {
      await task();
      await onChanged();
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusyId(null);
    }
  };

  const move = (block: GalaBlock, delta: -1 | 1) =>
    run(
      block.id,
      () =>
        reorderBlocks(
          eventId,
          moveId(
            blocks.map((b) => b.id),
            block.id,
            delta
          )
        ),
      'Không đổi được thứ tự khối.'
    );

  const toggleHidden = (block: GalaBlock) =>
    run(
      block.id,
      async () => {
        await saveBlock(eventId, { kind: block.kind, isHidden: !block.isHidden }, block.id);
        message.success(block.isHidden ? 'Đã hiện khối.' : 'Đã ẩn khối.');
      },
      'Không đổi được ẩn / hiện.'
    );

  const remove = (block: GalaBlock) =>
    run(
      block.id,
      async () => {
        await deleteBlock(eventId, block.id);
        message.success('Đã xoá khối.');
      },
      'Không xoá được khối.'
    );

  return (
    <div>
      <div className={styles.toolbar}>
        <Dropdown
          trigger={['click']}
          menu={{
            items: BLOCK_KINDS.map((kind) => ({
              key: kind,
              label: `${BLOCK_KIND_META[kind].text} — ${BLOCK_KIND_META[kind].hint}`,
            })),
            onClick: ({ key }) => openForm(null, key as GalaBlockKind),
          }}
        >
          <Button type="primary" icon={<PlusOutlined />}>
            Thêm khối
          </Button>
        </Dropdown>
      </div>

      {blocks.length === 0 ? (
        <Empty description="Chưa có khối nội dung nào. Bấm Thêm khối." />
      ) : (
        <div className={styles.blockList}>
          {blocks.map((block, index) => {
            const summary = blockSummary(block);
            return (
              <div
                key={block.id}
                className={`${styles.blockCard} ${block.isHidden ? styles.blockHidden : ''}`}
              >
                <div className={styles.blockHead}>
                  <Tag color="purple">{BLOCK_KIND_META[block.kind]?.text ?? block.kind}</Tag>
                  <span className={styles.blockTitle}>{block.title || '(không có tiêu đề)'}</span>
                  {block.isHidden && <Tag>Đang ẩn</Tag>}
                  <Tag color={block.visibility === 'ALL' ? undefined : 'gold'}>
                    {visibilityText(block)}
                  </Tag>
                  <Button
                    size="small"
                    icon={<ArrowUpOutlined />}
                    aria-label="Dời khối lên"
                    disabled={index === 0 || busyId !== null}
                    onClick={() => move(block, -1)}
                  />
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    aria-label="Dời khối xuống"
                    disabled={index === blocks.length - 1 || busyId !== null}
                    onClick={() => move(block, 1)}
                  />
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openForm(block, block.kind)}
                  >
                    Sửa
                  </Button>
                  <Button
                    size="small"
                    loading={busyId === block.id}
                    disabled={busyId !== null && busyId !== block.id}
                    onClick={() => toggleHidden(block)}
                  >
                    {block.isHidden ? 'Hiện' : 'Ẩn'}
                  </Button>
                  <Popconfirm
                    title="Xoá khối này?"
                    description="Xoá hẳn, không khôi phục được."
                    okText="Xoá"
                    okButtonProps={{ danger: true }}
                    cancelText="Không"
                    onConfirm={() => remove(block)}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label="Xoá khối"
                      disabled={busyId !== null}
                    />
                  </Popconfirm>
                </div>
                {summary && <p className={styles.blockPreview}>{summary}</p>}
              </div>
            );
          })}
        </div>
      )}

      <BlockForm
        open={formOpen}
        eventId={eventId}
        block={editing.block}
        kind={editing.kind}
        groupOptions={guestGroupOptions(
          templateGroups,
          blocks.flatMap((b) => b.groups ?? [])
        )}
        onClose={() => setFormOpen(false)}
        onSaved={onChanged}
      />
    </div>
  );
};

export default BlocksTab;
